import {
	Box,
	Flex,
	FormControl,
	FormLabel,
	HStack,
	Icon,
	Input,
	InputGroup,
	InputRightElement,
	Text,
	useToast,
} from '@chakra-ui/react';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import SessionContext from '../../contexts/SessionContext';
import API_URL from '../../constants/environment';
import { StepFooter } from './StepFooter';

interface TeslaLoginsStepProps {
	onNext: () => void;
	onSkip: () => void;
	onBack?: () => void;
}

interface TeslaCredEntry {
	_id: string;
	username: string;
	password: string | null;
}

export const TeslaLoginsStep = ({ onNext, onSkip, onBack }: TeslaLoginsStepProps) => {
	const toast = useToast();
	const sessionContext = useContext(SessionContext);
	const headers = { Authorization: sessionContext?.sessionId ?? '' };

	const [existingId, setExistingId] = useState<string | null>(null);
	const [username, setUsername] = useState('');
	const [password, setPassword] = useState('');
	const [showPassword, setShowPassword] = useState(false);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		const load = async () => {
			try {
				const { data } = await axios.get<{ tesla: TeslaCredEntry[] }>(
					`${API_URL}/settings/account/integrations`,
					{ headers }
				);
				const existing = (data.tesla ?? [])[0];
				if (existing) {
					setExistingId(existing._id);
					setUsername(existing.username ?? '');
					setPassword(existing.password ?? '');
				}
			} catch {
			} finally {
				setLoading(false);
			}
		};
		void load();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleSave = async () => {
		if (!username.trim() && !password.trim()) {
			onSkip();
			return;
		}
		if (!username.trim() || !password.trim()) {
			toast({
				title: 'Missing credentials',
				description: 'Enter both username and password.',
				status: 'warning',
				duration: 3000,
				isClosable: true,
			});
			return;
		}
		setSaving(true);
		try {
			await axios.post(
				`${API_URL}/settings/account/integrations`,
				{
					action: 'upsert',
					provider: 'tesla',
					credentials: [
						{
							...(existingId ? { _id: existingId } : {}),
							username: username.trim(),
							password: password.trim(),
						},
					],
				},
				{ headers }
			);
			toast({
				title: 'Tesla credentials saved',
				status: 'success',
				duration: 3000,
				isClosable: true,
			});
			onNext();
		} catch {
			toast({
				title: 'Failed to save Tesla credentials',
				status: 'error',
				duration: 4000,
				isClosable: true,
			});
		} finally {
			setSaving(false);
		}
	};

	return (
		<Flex direction="column" gap={4} flex={1} minH={0}>
			<Box
				borderWidth={1}
				borderColor="gray.200"
				borderRadius="md"
				bg="gray.50"
				px={4}
				py={4}
				flexShrink={0}
			>
				<Text fontSize="sm" color="gray.600" mb={3}>
					Tesla uses a single org-level login shared across all locations.
				</Text>
				<HStack spacing={4} align="end">
					<FormControl isRequired maxW="320px">
						<FormLabel fontSize="xs" mb={1}>Tesla Username</FormLabel>
						<Input
							size="sm"
							bg="white"
							value={username}
							onChange={(e) => setUsername(e.target.value)}
							placeholder="Tesla username"
							isDisabled={loading}
						/>
					</FormControl>
					<FormControl isRequired maxW="320px">
						<FormLabel fontSize="xs" mb={1}>Tesla Password</FormLabel>
						<InputGroup size="sm">
							<Input
								bg="white"
								type={showPassword ? 'text' : 'password'}
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								placeholder="Tesla password"
								isDisabled={loading}
							/>
							<InputRightElement>
								<Flex onClick={() => setShowPassword(!showPassword)} cursor="pointer">
									<Icon
										as={(showPassword ? FaEyeSlash : FaEye) as React.ElementType}
										color="gray.400"
										mr={2}
									/>
								</Flex>
							</InputRightElement>
						</InputGroup>
					</FormControl>
				</HStack>
			</Box>

			<Flex flex={1} />

			<StepFooter
				back={onBack ? { onClick: onBack } : undefined}
				secondaryActions={[{ label: 'Skip', onClick: onSkip }]}
				primary={{
					label: 'Save Tesla Credentials',
					onClick: () => void handleSave(),
					isLoading: saving,
					loadingText: 'Saving...',
				}}
			/>
		</Flex>
	);
};
